import React, { memo, useState, useMemo } from 'react';
import { useLang } from '../../i18n';
import { useLists } from '../../context';
import { useListNames } from '../../hooks/useListNames';
import { keepInView } from '../shared/dom';
import { normalizeName } from '../../logic/names';

/**
 * A list panel's one add surface — a search-or-add checklist ranked by frecency (see
 * buildCatalogs). Idle: addable staples. Searching: every match, incl. items already
 * listed (shown ✓ "on the list"), then matches that live in OTHER lists (tap adds it to
 * its own list), plus an "Add …" row when nothing matches — which says where a name will
 * land if it belongs in another list (e.g. "Besan → Chef").
 *
 *   catalog         [{ name, count, fromHistory }] — this list's suggestions, best first
 *   taken           Set of normalized names already on this list
 *   onAdd(name)     add a suggestion or a typed name
 *   elsewhere(nq)   matches in other lists → [{ name, listId, on }]
 *   onAddElsewhere(listId, name) · destOf(name) → the list a typed name lands in (or null)
 * Memoized: the panel passes stable callbacks, so it re-renders only with its own list.
 */
export const AddCatalog = memo(function AddCatalog({ catalog, taken, color, onAdd, placeholder, elsewhere, onAddElsewhere, destOf }) {
  const { t, ln } = useLang();
  const { short } = useListNames();
  const { catById } = useLists();
  const [q, setQ] = useState('');
  const nq = normalizeName(q);
  // Idle → addable staples only (best first). Searching → every match, incl. items
  // already on the list (flagged `on`), so nothing you've added ever hides.
  const results = useMemo(() => {
    if (!nq) return catalog.filter(s => !taken.has(normalizeName(s.name))).map(s => ({ ...s, on: false }));
    return catalog
      .filter(s => normalizeName(s.name).includes(nq))
      .map(s => ({ ...s, on: taken.has(normalizeName(s.name)) }));
  }, [catalog, taken, nq]);
  const others = useMemo(() => (nq && elsewhere ? elsewhere(nq) : []), [elsewhere, nq]);
  const known = nq && (catalog.some(s => normalizeName(s.name) === nq) || others.some(o => normalizeName(o.name) === nq));
  const canAdd = nq && !known && !taken.has(nq);
  const dest = canAdd && destOf ? destOf(q.trim()) : null;
  const pick = name => { onAdd(name); setQ(''); };
  const pickElsewhere = o => { onAddElsewhere(o.listId, o.name); setQ(''); };
  const submit = () => {
    const clean = q.trim();
    if (!clean) return;
    // Enter on a name that lives in another list adds it there, like tapping its row.
    const other = others.find(o => normalizeName(o.name) === nq);
    if (other && !other.on) { pickElsewhere(other); return; }
    pick(clean);
  };
  const chip = id => { const l = catById(id); return <span className="do-cat-item__list" style={{ '--lc': l.color }}>{l.icon} {short(l)}</span>; };
  const onRow = (name, extra) => (
    <div className="do-cat-item do-cat-item--on" title={t('today.alreadyOnList')}>
      <span className="do-cat-item__pick do-cat-item__pick--static">
        <span className="do-cat-item__ico">✓</span>
        <span className="do-cat-item__name">{ln(name)}</span>
        {extra}
      </span>
    </div>
  );

  return (
    <div className="do-catlist" style={{ '--cc': color }}>
      <input className="do-catlist__search" placeholder={placeholder || t('today.searchGeneric')} value={q}
        onChange={e => setQ(e.target.value)} onFocus={keepInView}
        onKeyDown={e => { if (e.key === 'Enter') submit(); }} aria-label={t('today.searchAria')} />
      {results.length > 0 && !q && <p className="do-catlist__lbl">{t('today.usuallyNeeded')}</p>}
      <div className="do-catlist__items">
        {canAdd && (
          <div className="do-cat-item">
            <button className="do-cat-item__pick do-cat-item__pick--new" onClick={() => pick(q.trim())}>
              <span className="do-cat-item__ico">＋</span>
              <span className="do-cat-item__name">{t('today.addRow', { q: q.trim() })}</span>
              {dest && chip(dest)}
            </button>
          </div>
        )}
        {results.map(s => (s.on
          ? <React.Fragment key={s.name}>{onRow(s.name, <span className="do-cat-item__note">{t('today.onList')}</span>)}</React.Fragment>
          : (
            <div key={s.name} className="do-cat-item">
              <button className="do-cat-item__pick" onClick={() => pick(s.name)}
                title={s.fromHistory ? t('today.neededRecent', { n: s.count }) : t('today.commonItem')}>
                <span className="do-cat-item__ico">＋</span>
                <span className="do-cat-item__name">{ln(s.name)}</span>
              </button>
            </div>
          )))}
        {others.length > 0 && <p className="do-catlist__lbl do-catlist__lbl--other">{t('today.otherLists')}</p>}
        {others.map(o => (o.on
          ? <React.Fragment key={`${o.listId}:${o.name}`}>{onRow(o.name, chip(o.listId))}</React.Fragment>
          : (
            <div key={`${o.listId}:${o.name}`} className="do-cat-item">
              <button className="do-cat-item__pick" onClick={() => pickElsewhere(o)}
                title={t('today.addToList', { list: short(o.listId) })}>
                <span className="do-cat-item__ico">＋</span>
                <span className="do-cat-item__name">{ln(o.name)}</span>
                {chip(o.listId)}
              </button>
            </div>
          )))}
        {results.length === 0 && others.length === 0 && !canAdd && (
          <p className="do-catlist__hint">{q ? t('today.nothingFound') : t('today.typeToAdd')}</p>
        )}
      </div>
    </div>
  );
});
